import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Card,
  Button,
  Input,
  Tag,
  Typography,
  message,
  Row,
  Col,
  Modal,
  Space,
  Tooltip,
  Select,
} from 'antd';
import {
  StopOutlined,
  CheckCircleOutlined,
  SearchOutlined,
  ReloadOutlined,
  ExclamationCircleOutlined,
  UserDeleteOutlined,
  PhoneOutlined,
  MailOutlined,
  FilterOutlined,
  UsergroupDeleteOutlined,
} from '@ant-design/icons';
import OptOutService from '../../../../services/optoutService';
import WriteGuard from '../../../../components/WriteGuard';

const { Text } = Typography;

const CATEGORY_OPTIONS = [
  { value: '', label: 'All Categories' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'utility', label: 'Utility' },
  { value: 'authentication', label: 'Authentication' },
];

/**
 * OptedOutContactsPage — Admin page to view & manage WhatsApp campaign opt-outs.
 *
 * Features:
 * - Paginated table of opted-out contacts
 * - Search by phone / name
 * - Filter by category (marketing / utility / authentication)
 * - Re-subscribe (opt-in) action per row (category-aware)
 * - Manual opt-out action with category selection
 * - Stats cards (total opted-out)
 */
const OptedOutContactsPage = () => {
  // ─── State ─────────────────────────────────────────────
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [searchText, setSearchText] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [actionLoading, setActionLoading] = useState({});

  // Manual opt-out modal
  const [optOutModalVisible, setOptOutModalVisible] = useState(false);
  const [manualPhone, setManualPhone] = useState('');
  const [manualCategory, setManualCategory] = useState('marketing');
  const [manualOptOutLoading, setManualOptOutLoading] = useState(false);

  // ─── Fetch data ────────────────────────────────────────
  const fetchOptedOutContacts = useCallback(async (page = currentPage, size = pageSize, search = searchText, category = categoryFilter) => {
    setLoading(true);
    try {
      const data = await OptOutService.listOptedOut({ page, limit: size, search, category });
      if (data.success) {
        setContacts(data.data?.contacts || data.contacts || data.data || []);
        setTotalRecords(data.data?.total || data.total || 0);
      } else {
        message.error(data.message || 'Failed to load opted-out contacts');
      }
    } catch (err) {
      console.error('Error fetching opted-out contacts:', err);
      message.error('Failed to load opted-out contacts');
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchText, categoryFilter]);

  useEffect(() => {
    fetchOptedOutContacts(currentPage, pageSize, searchText, categoryFilter);
  }, [currentPage, pageSize, categoryFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Handlers ──────────────────────────────────────────
  const handleSearch = (value) => {
    setSearchText(value);
    setCurrentPage(1);
    fetchOptedOutContacts(1, pageSize, value, categoryFilter);
  };

  const handleCategoryFilter = (value) => {
    setCategoryFilter(value);
    setCurrentPage(1);
  };

  const handleTableChange = (pagination) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
  };

  const handleOptIn = (phoneNumber, category = null) => {
    const categoryLabel = category
      ? category.charAt(0).toUpperCase() + category.slice(1)
      : 'all categories';
    Modal.confirm({
      title: 'Re-subscribe Contact',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>Are you sure you want to re-subscribe <strong>{phoneNumber}</strong>{category ? ` to ${categoryLabel} campaigns` : ' to all campaigns'}?</p>
          <p style={{ color: '#666', fontSize: 13 }}>
            {category
              ? `This contact will start receiving ${categoryLabel.toLowerCase()} campaign messages again.`
              : 'This contact will start receiving all campaign messages again.'}
          </p>
        </div>
      ),
      okText: category ? `Re-subscribe to ${categoryLabel}` : 'Re-subscribe to All',
      okType: 'primary',
      cancelText: 'Cancel',
      onOk: async () => {
        const key = `${phoneNumber}-${category || 'all'}`;
        setActionLoading(prev => ({ ...prev, [key]: true }));
        try {
          const data = await OptOutService.optIn(phoneNumber, category);
          if (data.success) {
            message.success(`${phoneNumber} has been re-subscribed${category ? ` to ${categoryLabel.toLowerCase()} campaigns` : ' to all campaigns'}`);
            fetchOptedOutContacts();
          } else {
            message.error(data.message || 'Failed to re-subscribe contact');
          }
        } catch (err) {
          console.error('Error opting in contact:', err);
          message.error('Failed to re-subscribe contact');
        } finally {
          setActionLoading(prev => ({ ...prev, [key]: false }));
        }
      },
    });
  };

  const handleManualOptOut = async () => {
    const phone = manualPhone.replace(/[^0-9]/g, '');
    if (!phone || phone.length < 10) {
      message.warning('Please enter a valid phone number (E.164 without +)');
      return;
    }
    setManualOptOutLoading(true);
    try {
      const data = await OptOutService.optOut(phone, manualCategory || null);
      if (data.success) {
        const catLabel = manualCategory
          ? manualCategory.charAt(0).toUpperCase() + manualCategory.slice(1)
          : 'all';
        message.success(`${phone} has been opted out of ${catLabel.toLowerCase()} campaigns`);
        setOptOutModalVisible(false);
        setManualPhone('');
        setManualCategory('marketing');
        fetchOptedOutContacts();
      } else {
        message.error(data.message || 'Failed to opt-out contact');
      }
    } catch (err) {
      console.error('Error opting out contact:', err);
      message.error('Failed to opt-out contact');
    } finally {
      setManualOptOutLoading(false);
    }
  };

  // ─── Table columns ────────────────────────────────────
  const columns = [
    {
      title: 'Phone Number',
      dataIndex: 'phone_number',
      key: 'phone_number',
      render: (text) => (
        <Space size={6}>
          <PhoneOutlined style={{ color: '#2563eb', fontSize: 12 }} />
          <Text copyable={{ tooltips: ['Copy', 'Copied!'] }} style={{ fontWeight: 600, fontSize: 13, color: '#1e293b' }}>{text}</Text>
        </Space>
      ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => text || record.username
        ? <span style={{ fontSize: 13, color: '#334155' }}>{text || record.username}</span>
        : <Text type="secondary" style={{ fontSize: 12 }}>—</Text>,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (text, record) => {
        const email = text || record.emailid;
        return email ? (
          <Space size={5}>
            <MailOutlined style={{ color: '#94a3b8', fontSize: 11 }} />
            <Text style={{ fontSize: 12, color: '#475569' }}>{email}</Text>
          </Space>
        ) : <Text type="secondary" style={{ fontSize: 12 }}>—</Text>;
      },
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: 130,
      render: (cat) => {
        if (!cat) return (
          <Tag style={{ fontSize: 11, borderRadius: 20, padding: '1px 9px', margin: 0, borderColor: '#e2e8f0', color: '#64748b', background: '#f8fafc' }}>Global</Tag>
        );
        const colorMap = {
          marketing: { bg: '#fff7ed', border: '#fed7aa', color: '#c2410c' },
          utility: { bg: '#eff6ff', border: '#bfdbfe', color: '#1d4ed8' },
          authentication: { bg: '#f5f3ff', border: '#ddd6fe', color: '#6d28d9' },
        };
        const style = colorMap[cat] || { bg: '#f1f5f9', border: '#e2e8f0', color: '#475569' };
        return (
          <Tag style={{ fontSize: 11, borderRadius: 20, padding: '1px 9px', margin: 0, background: style.bg, borderColor: style.border, color: style.color, fontWeight: 600 }}>
            {cat.charAt(0).toUpperCase() + cat.slice(1)}
          </Tag>
        );
      },
    },
    {
      title: 'Status',
      key: 'status',
      width: 110,
      render: () => (
        <Tag
          icon={<StopOutlined style={{ fontSize: 10 }} />}
          style={{ fontSize: 11, borderRadius: 20, padding: '1px 9px', margin: 0, background: '#fff1f2', borderColor: '#fecdd3', color: '#be123c', fontWeight: 600 }}
        >
          Opted Out
        </Tag>
      ),
    },
    {
      title: 'Opted Out Since',
      dataIndex: 'opted_out_at',
      key: 'opted_out_at',
      width: 160,
      render: (text) => text
        ? <span style={{ fontSize: 12, color: '#64748b' }}>{new Date(text).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
        : <Text type="secondary" style={{ fontSize: 12 }}>—</Text>,
      sorter: (a, b) => new Date(a.opted_out_at) - new Date(b.opted_out_at),
    },
    {
      title: 'Action',
      key: 'action',
      width: 140,
      render: (_, record) => {
        const cat = record.category;
        const key = `${record.phone_number}-${cat || 'all'}`;
        return (
          <WriteGuard page="campaigns">
            <Tooltip title={cat ? `Re-subscribe to ${cat} campaigns` : 'Re-subscribe to all campaigns'}>
              <Button
                size="small"
                icon={<CheckCircleOutlined />}
                loading={actionLoading[key]}
                onClick={() => handleOptIn(record.phone_number, cat)}
                style={{
                  borderRadius: 7, fontSize: 12, height: 28,
                  background: '#f0fdf4', borderColor: '#bbf7d0', color: '#15803d',
                  fontWeight: 600,
                }}
              >
                Re-subscribe
              </Button>
            </Tooltip>
          </WriteGuard>
        );
      },
    },
  ];

  // ─── Render ────────────────────────────────────────────
  return (
    <div style={{ maxWidth: '100%' }}>

      {/* ── Page Header ────────────────────────────────── */}
      <div style={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: 12, marginBottom: 20,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 42, height: 42, borderRadius: 11,
            background: 'linear-gradient(135deg, #fee2e2, #fecaca)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <UsergroupDeleteOutlined style={{ color: '#dc2626', fontSize: 20 }} />
          </div>
          <div>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: '#0f172a', margin: 0, lineHeight: 1.2, letterSpacing: '-0.02em' }}>
              Opted-Out Contacts
            </h2>
            <Text type="secondary" style={{ fontSize: 13, lineHeight: 1.4 }}>
              Contacts who sent "STOP" are opted out per category. They can re-join by sending "START".
            </Text>
          </div>
        </div>

        {/* Stat chip — top right */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          background: '#fff1f2', border: '1px solid #fecdd3',
          borderRadius: 10, padding: '8px 14px',
        }}>
          <UserDeleteOutlined style={{ color: '#dc2626', fontSize: 16 }} />
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#9f1239', textTransform: 'uppercase', letterSpacing: '0.05em', lineHeight: 1 }}>Total Opted-Out</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#dc2626', lineHeight: 1.1 }}>{totalRecords}</div>
          </div>
        </div>
      </div>

      {/* ── Toolbar ─────────────────────────────────────── */}
      <div style={{
        background: '#fff', border: '1px solid #e8ecf1',
        borderRadius: 12, padding: '10px 14px', marginBottom: 14,
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
      }}>
        <Row gutter={[10, 10]} align="middle">
          <Col xs={24} sm={10} md={9} lg={8}>
            <Input
              placeholder="Search by phone number or name..."
              allowClear
              prefix={<SearchOutlined style={{ color: '#94a3b8', fontSize: 13 }} />}
              onPressEnter={(e) => handleSearch(e.target.value)}
              onChange={(e) => { if (!e.target.value) handleSearch(''); }}
              onBlur={(e) => handleSearch(e.target.value)}
              defaultValue={searchText}
              style={{ borderRadius: 8, fontSize: 13 }}
            />
          </Col>
          <Col xs={24} sm={7} md={6}>
            <Select
              value={categoryFilter}
              onChange={handleCategoryFilter}
              style={{ width: '100%', borderRadius: 8 }}
              suffixIcon={<FilterOutlined style={{ fontSize: 12 }} />}
              options={CATEGORY_OPTIONS}
              size="middle"
            />
          </Col>
          <Col xs={24} sm={7} md={9} style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => fetchOptedOutContacts()}
              loading={loading}
              style={{ borderRadius: 8, fontSize: 13 }}
            >
              Refresh
            </Button>
            <WriteGuard page="campaigns">
              <Button
                type="primary"
                danger
                icon={<StopOutlined />}
                onClick={() => setOptOutModalVisible(true)}
                style={{ borderRadius: 8, fontSize: 13, fontWeight: 600 }}
              >
                Manual Opt-Out
              </Button>
            </WriteGuard>
          </Col>
        </Row>
      </div>

      {/* ── Table ───────────────────────────────────────── */}
      <div style={{
        background: '#fff', border: '1px solid #e8ecf1',
        borderRadius: 12, overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
      }}>
        <Table
          columns={columns}
          dataSource={contacts}
          rowKey={(record) => `${record.phone_number}-${record.category || 'global'}`}
          loading={loading}
          onChange={handleTableChange}
          scroll={{ x: 780 }}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: totalRecords,
            showSizeChanger: true,
            showQuickJumper: false,
            showTotal: (total, range) => `${range[0]}–${range[1]} of ${total} contacts`,
            pageSizeOptions: ['10', '20', '50', '100'],
            style: { margin: 0, padding: '10px 14px', borderTop: '1px solid #f0f2f5' },
          }}
          size="small"
          style={{ fontSize: 13 }}
          rowClassName={() => 'optout-row'}
          locale={{
            emptyText: (
              <div style={{ padding: '48px 24px', textAlign: 'center' }}>
                <div style={{
                  width: 56, height: 56, borderRadius: '50%',
                  background: '#f8fafc', border: '1px solid #e2e8f0',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  margin: '0 auto 12px',
                }}>
                  <UsergroupDeleteOutlined style={{ fontSize: 22, color: '#cbd5e1' }} />
                </div>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#475569', marginBottom: 4 }}>No opted-out contacts</div>
                <div style={{ fontSize: 12, color: '#94a3b8' }}>Contacts who send "STOP" will appear here.</div>
              </div>
            ),
          }}
        />
      </div>

      {/* ── Manual Opt-Out Modal ─────────────────────────── */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 34, height: 34, borderRadius: 9,
              background: 'linear-gradient(135deg, #fee2e2, #fca5a5)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <StopOutlined style={{ color: '#dc2626', fontSize: 16 }} />
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', lineHeight: 1.2 }}>Manual Opt-Out</div>
              <div style={{ fontSize: 12, color: '#94a3b8', fontWeight: 400 }}>Opt out a contact from a campaign category</div>
            </div>
          </div>
        }
        open={optOutModalVisible}
        onCancel={() => {
          setOptOutModalVisible(false);
          setManualPhone('');
          setManualCategory('marketing');
        }}
        onOk={handleManualOptOut}
        confirmLoading={manualOptOutLoading}
        okText="Opt Out Contact"
        okButtonProps={{ danger: true, style: { borderRadius: 8, fontWeight: 600 } }}
        cancelButtonProps={{ style: { borderRadius: 8 } }}
        width={440}
        styles={{ body: { paddingTop: 4 } }}
      >
        <div style={{
          background: '#fffbeb', border: '1px solid #fde68a',
          borderRadius: 8, padding: '9px 12px', marginBottom: 16,
          display: 'flex', gap: 8, alignItems: 'flex-start',
        }}>
          <ExclamationCircleOutlined style={{ color: '#d97706', fontSize: 14, marginTop: 1, flexShrink: 0 }} />
          <Text style={{ fontSize: 12, color: '#92400e', lineHeight: 1.5 }}>
            Enter the phone in E.164 format without "+" (e.g. <strong>919021522219</strong>). The contact will stop receiving messages in the selected category.
          </Text>
        </div>

        <Space direction="vertical" style={{ width: '100%' }} size={14}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 }}>Phone Number</div>
            <Input
              placeholder="e.g. 919021522219"
              value={manualPhone}
              onChange={(e) => setManualPhone(e.target.value)}
              prefix={<PhoneOutlined style={{ color: '#94a3b8' }} />}
              style={{ borderRadius: 8, fontSize: 13 }}
              onPressEnter={handleManualOptOut}
            />
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 }}>Category</div>
            <Select
              value={manualCategory}
              onChange={setManualCategory}
              style={{ width: '100%', borderRadius: 8 }}
              size="middle"
              options={[
                { value: 'marketing', label: 'Marketing — Promotional campaigns' },
                { value: 'utility', label: 'Utility — Transactional messages' },
                { value: 'authentication', label: 'Authentication — OTP / verification' },
              ]}
            />
          </div>
        </Space>
      </Modal>

      {/* ── Row hover style ──────────────────────────────── */}
      <style>{`
        .optout-row:hover > td { background: #f8fafc !important; }
        .ant-table-thead > tr > th {
          background: #f9fafb !important;
          font-size: 11px !important;
          font-weight: 700 !important;
          color: #6b7280 !important;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          padding: 9px 12px !important;
          border-bottom: 1px solid #e8ecf1 !important;
        }
        .ant-table-tbody > tr > td { padding: 9px 12px !important; }
        .ant-table-tbody > tr:last-child > td { border-bottom: none !important; }
      `}</style>
    </div>
  );
};

export default OptedOutContactsPage;
